import { defineRouting } from "next-intl/routing"

// BOOTH localization — English is the source/default, Spanish (es) is the
// second surface. `as-needed` keeps English un-prefixed (`/`, `/pricing`)
// and prefixes Spanish (`/es`, `/es/pricing`).
//
// localeDetection is FALSE on purpose: an `es` Accept-Language header must
// NOT force a redirect to /es. Instead the marketing layout shows a small
// dismissible "¿Prefieres español?" banner and the user opts in. The choice
// is then persisted in the NEXT_LOCALE cookie (next-intl's default cookie).
export const routing = defineRouting({
  locales: ["en", "es"],
  defaultLocale: "en",
  localePrefix: "as-needed",
  localeDetection: false,
})

export type Locale = (typeof routing.locales)[number]

// Every PUBLIC MARKETING route lives under app/[locale]/ and is served in
// both languages. This list drives (a) which paths the next-intl middleware
// handles in proxy.ts and (b) which hrefs get a locale prefix in LocaleLink.
// App-zone routes (/dashboard, /auth, /api, /shop, /payment, /setup,
// /invite, /preview) are preference-based (cookie) and NEVER carry a prefix.
// Adding a new marketing page? Put it in app/[locale]/ AND list it here —
// missing it here means the un-prefixed URL 404s.
export const marketingPathnames = [
  "/",
  "/pricing",
  "/blog",
  "/articles",
  "/changelog",
  "/community",
  "/press",
  "/traction",
  "/hire",
  "/hiring",
  "/privacy",
  "/terms",
  "/refunds",
  "/sms",
  "/rewards",
  "/help",
  "/barbers",
  "/listings",
  "/compare",
  "/search",
  "/booth-rental",
] as const

// "/es/pricing" -> "/pricing", "/es" -> "/", "/pricing" -> "/pricing".
// Use this before ANY pathname comparison in shared/global chrome — a check
// like `pathname === "/"` silently misses the Spanish landing at /es.
export function stripLocalePrefix(pathname: string): string {
  return pathname.replace(/^\/(en|es)(?=\/|$)/, "") || "/"
}

export function isMarketingPath(pathname: string): boolean {
  const stripped = stripLocalePrefix(pathname)
  return marketingPathnames.some((p) =>
    p === "/" ? stripped === "/" : stripped === p || stripped.startsWith(`${p}/`),
  )
}

// Marketing CONTENT pages — the subset a SIGNED-IN visitor still reads as
// marketing rather than as part of the app, so PublicNavbar stays up for them
// instead of self-suppressing.
//
// Narrower than marketingPathnames on purpose, which also lists /barbers,
// /listings, /hiring and /community. Those are browse surfaces: signed in, you
// are using the product there, and the app chrome (floating nav, bell) is the
// right frame. These are pages you READ, and a signed-in reader needs the same
// way back to Pricing / Blog / the rest of the site that a logged-out one gets.
//
// /changelog is in the list now that it renders PublicNavbar too — it was the
// last page still carrying a hand-built header.
const marketingContentPathnames = [
  "/pricing",
  "/blog",
  "/articles",
  "/press",
  "/traction",
  "/privacy",
  "/terms",
  "/refunds",
  "/sms",
  "/rewards",
  "/changelog",
  "/compare",
  "/booth-rental",
  "/help",
  // Full-screen chat-style search: its own glass nav carries the single
  // Join CTA, so the floating dock launcher and the corner Sign Up pill
  // would double up on a page built to look like one focused surface.
  "/search",
] as const

/**
 * A marketing page that keeps its public navbar even when signed in.
 *
 * Also means "this surface does not follow the app's dark mode" — these pages
 * are light (or declare their own data-nav-theme) whatever the user's theme is,
 * which is why the wordmark can't be swapped by an html.dark CSS rule here.
 */
export function isMarketingContentPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  const stripped = stripLocalePrefix(pathname)
  return marketingContentPathnames.some((p) => stripped === p || stripped.startsWith(`${p}/`))
}

/**
 * Pages whose chrome is PublicNavGlass — the floating pill that carries the
 * whole site inside its menu panel.
 *
 * This is the landing plus every marketing content page. It exists because
 * that panel IS the navigation: any other global chrome on the same screen
 * (the FloatingNav launcher, BrandHeader's wordmark, the PublicAuthCta) is a
 * second menu competing with it. Those components each carried their own
 * hand-kept list of exempt paths — one had ["/"], three had
 * ["/", "/changelog", "/traction"] — which is exactly the kind of thing that
 * drifts. They all ask this instead now.
 */
export function isGlassNavPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  return stripLocalePrefix(pathname) === "/" || isMarketingContentPath(pathname)
}

// PUBLIC BOOKING surfaces — client-facing pages a logged-out visitor
// usually reaches via a barber's link or QR code: a barber's public
// booking profile (/barbers/<slug>, NOT the /barbers index), storefront
// booking pages (/book/*), and public shop pages (/shop/*). Per PRD §11
// the logged-out chrome on these surfaces must NOT show the pro-marketing
// menu (Blog / Job Board / Browse Shops / Community, pro pricing) — the
// default audience here is a booking client.
export function isPublicBookingSurface(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  const stripped = stripLocalePrefix(pathname)
  if (/^\/barbers\/[^/]+/.test(stripped)) return true
  if (stripped === "/book" || stripped.startsWith("/book/")) return true
  if (stripped === "/shop" || stripped.startsWith("/shop/")) return true
  return false
}

// FORCE-DARK booking surfaces — the client-facing booking pages a barber shares
// (their primary profile /book/[slug] and simple link /book/quick/[slug]) render
// as a deliberately DARK storefront for EVERY visitor, regardless of device
// theme, so the barber's page always looks the way they want to show it off.
// /book/shops/* follow the real app theme, so they're excluded. Used both to
// scope the `.force-dark` page wrapper and to tell the layout-level chrome
// (signup pill, wordmark, notification bell) to render its dark styling here.
export function isForceDarkBooking(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  const p = stripLocalePrefix(pathname)
  if (p !== "/book" && !p.startsWith("/book/")) return false
  if (p === "/book/shops" || p.startsWith("/book/shops/")) return false
  const seg = p.split("/").filter(Boolean) // ["book", <slug|"quick">, ...]
  return seg.length === 2 || seg[1] === "quick" // /book/[slug] or /book/quick/[slug]
}
